import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from 'recharts';
import { studentAPI, recruiterAPI, notificationAPI } from '../services/api';
import { validateEmailForSubmission } from '../utils/validation';
import EmailInput from '../components/EmailInput';
import TwoFactorSettings from '../components/TwoFactorSettings';
import Pagination from '../components/Pagination';
import { QRCodeSVG } from 'qrcode.react';

function RecruiterDashboard() {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/students" element={<Students />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/payments" element={<Payments />} />
      <Route path="/notifications" element={<Notifications />} />
      <Route path="/profile" element={<Profile />} />
    </Routes>
  );
}

function Dashboard() {
  const [commissions, setCommissions] = useState(null);
  const [monthlyPerformance, setMonthlyPerformance] = useState([]);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [commissionsResponse, monthlyResponse, notificationsResponse] = await Promise.all([
        studentAPI.getCommissions(),
        studentAPI.getMonthlyPerformance(),
        notificationAPI.getRecent(4)
      ]);
      setCommissions(commissionsResponse.data);
      setMonthlyPerformance(monthlyResponse.data);
      setRecentNotifications(notificationsResponse.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  };

  // Enhanced data for charts - use real data from backend
  const monthlyData = monthlyPerformance?.map(item => ({
    month: item.month_name,
    admissions: parseInt(item.admissions) || 0,
    enrollments: parseInt(item.enrollments) || 0,
    earnings: parseFloat(item.earnings) || 0
  })) || [
      { month: 'Jan', admissions: 0, enrollments: 0, earnings: 0 },
      { month: 'Feb', admissions: 0, enrollments: 0, earnings: 0 },
      { month: 'Mar', admissions: 0, enrollments: 0, earnings: 0 },
      { month: 'Apr', admissions: 0, enrollments: 0, earnings: 0 },
      { month: 'May', admissions: 0, enrollments: 0, earnings: 0 },
      { month: 'Jun', admissions: 0, enrollments: 0, earnings: 0 }
    ];

  const performanceData = [
    { name: 'Admissions', value: commissions?.admissions.count || 0, color: '#3b82f6' },
    { name: 'Enrollments', value: commissions?.enrollments.count || 0, color: '#10b981' }
  ].filter(item => item.value > 0); // Only show items with values > 0

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>

          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Your Dashboard...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching your performance metrics and earnings</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Dashboard</h1>
          <p className="page-subtitle">Track your recruitment performance, earnings, and student progress</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <button className="btn btn-outline btn-sm">View Analytics</button>
        </div>
      </div>

      {/* Enhanced Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Admissions</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">{commissions?.admissions.count || 0}</div>
          <div style={{ fontSize: '14px', color: '#3b82f6', fontWeight: '600', marginTop: '8px' }}>
            KES {Number(commissions?.admissions.total || 0).toFixed(2)}
          </div>
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
            KES 500 per admission
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Session Registrations</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">{commissions?.enrollments.count || 0}</div>
          <div style={{ fontSize: '14px', color: '#10b981', fontWeight: '600', marginTop: '8px' }}>
            KES {Number(commissions?.enrollments.total || 0).toFixed(2)}
          </div>
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
            KES 300 per registration
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Earnings</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">KES {Number(commissions?.cumulativeTotal || 0).toFixed(2)}</div>
          <div className="stat-card-change positive">+28% this month</div>
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
            Target: KES 15,000
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Performance Score</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">
            {commissions?.admissions.count > 0
              ? Math.min(Math.round((commissions.enrollments.count / commissions.admissions.count) * 100), 100)
              : 0}%
          </div>
          <div className="stat-card-change positive">+12% from last month</div>
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
            Registration conversion rate
          </div>
        </div>

      </div>

      {/* Enhanced Dashboard Grid */}
      <div className="dashboard-grid">
        <div className="chart-container">
          <div className="card-header">
            <div>
              <h2 className="card-title">Monthly Performance</h2>
              <p className="card-subtitle">Your recruitment and earnings trend (last 6 months - live data)</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <span style={{
                fontSize: '12px',
                backgroundColor: '#037b90',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontWeight: '600'
              }}>
                📊 Live Data
              </span>
              <button className="btn btn-outline btn-sm">Detailed View</button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {monthlyData.length > 0 && monthlyData.some(item => item.admissions > 0 || item.enrollments > 0 || item.earnings > 0) ? (
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="earningsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#037b90" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#037b90" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} fontWeight={600} />
                <YAxis stroke="#64748b" fontSize={12} fontWeight={600} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    fontWeight: '600'
                  }}
                  formatter={(value, name) => {
                    if (name === 'Earnings (KES)') {
                      return [`KES ${Number(value).toFixed(2)}`, 'Earnings'];
                    }
                    return [value, name];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="earnings"
                  stroke="#037b90"
                  strokeWidth={3}
                  fill="url(#earningsGradient)"
                  name="Earnings (KES)"
                />
              </AreaChart>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📈</div>
                <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Performance Data</h3>
                <p style={{ fontSize: '14px', textAlign: 'center' }}>
                  Your monthly performance will appear here once you have admissions and enrollments
                </p>
              </div>
            )}
          </ResponsiveContainer>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Commission Structure</h2>
                <p className="card-subtitle">Earnings breakdown per student milestone</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', color: '#3b82f6' }}>Student Admission</span>
                  <span style={{ fontWeight: '700', fontSize: '18px', color: '#3b82f6' }}>KES 500</span>
                </div>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                  Earned when a student you recruited gets admitted to OUK
                </p>
              </div>

              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', color: '#10b981' }}>Session Registration</span>
                  <span style={{ fontWeight: '700', fontSize: '18px', color: '#10b981' }}>KES 300</span>
                </div>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                  Additional bonus when the same student registers for classes
                </p>
              </div>

              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(3, 123, 144, 0.1), rgba(3, 123, 144, 0.05))',
                borderRadius: '12px',
                border: '1px solid rgba(3, 123, 144, 0.2)',
                marginTop: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', color: '#037b90' }}>Maximum per Student</span>
                  <span style={{ fontWeight: '700', fontSize: '20px', color: '#037b90' }}>KES 800</span>
                </div>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                  Total possible earnings when a student completes both milestones
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Performance Breakdown</h2>
                <p className="card-subtitle">Your recruitment statistics (live data)</p>
              </div>
              <span style={{
                fontSize: '10px',
                backgroundColor: '#10b981',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '8px',
                fontWeight: '600'
              }}>
                📊 Live
              </span>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              {performanceData.length > 0 ? (
                <PieChart>
                  <Pie
                    data={performanceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {performanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontWeight: '600'
                    }}
                  />
                </PieChart>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#64748b'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}>📊</div>
                  <h4 style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px' }}>No Performance Data</h4>
                  <p style={{ fontSize: '12px', textAlign: 'center' }}>
                    Start recruiting to see your performance breakdown
                  </p>
                </div>
              )}
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
              {performanceData.length > 0 ? (
                performanceData.map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: item.color }}></div>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>{item.name}</span>
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: item.color }}>{item.value}</span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>
                  Performance metrics will appear here once you start recruiting
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Recent Activity Timeline */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Recent Activity</h2>
            <p className="card-subtitle">Your latest recruitment activities and achievements</p>
          </div>
          <Link to="/recruiter/notifications" className="btn btn-outline btn-sm">View All</Link>
        </div>
        <div>
          {recentNotifications.length > 0 ? (
            recentNotifications.map((notification) => (
              <div key={notification.id} className="activity-item">
                <div className="activity-title">{notification.title}</div>
                <div className="activity-description">{notification.message}</div>
                <div className="activity-time">{formatTimeAgo(notification.created_at)}</div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🔔</div>
              <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Recent Activity</h3>
              <p style={{ fontSize: '14px' }}>Your recruitment activities will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('admissions');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, page: 1 });
  const [commissions, setCommissions] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentModal, setShowStudentModal] = useState(false);

  useEffect(() => {
    loadStudents();
  }, [activeTab, currentPage]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const type = activeTab === 'admissions' ? 'admission' : 'enrollment';

      const [studentsResponse, commissionsResponse] = await Promise.all([
        studentAPI.getMyStudents(currentPage, pagination.limit, type),
        studentAPI.getCommissions()
      ]);

      setStudents(studentsResponse.data.students || []);
      setPagination(studentsResponse.data.pagination || { total: 0, limit: 20, page: 1 });
      setCommissions(commissionsResponse.data);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = (studentId) => {
    // Generate mock student details with phone number
    const studentDetails = {
      id: studentId,
      name: `Student ${studentId}`,
      phone: `+254${Math.floor(Math.random() * 900000000) + 100000000}`,
      email: `student${studentId.toLowerCase()}@example.com`,
      program: activeTab === 'admissions' ? 'Business Administration' : 'Computer Science'
    };
    setSelectedStudent(studentDetails);
    setShowStudentModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Phone number copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy phone number');
    });
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>

          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Your Students...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching student records and performance data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Students</h1>
          <p className="page-subtitle">Students you have successfully recruited and their academic progress</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <button className="btn btn-secondary btn-sm">Analytics</button>
        </div>
      </div>

      {/* Enhanced Stats Grid */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Students</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">{commissions ? (commissions.admissions?.count || 0) + (commissions.enrollments?.count || 0) : 0}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            All recruited students
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Admissions</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">{commissions?.admissions?.count || 0}</div>
          <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600', marginTop: '8px' }}>
            {Math.floor(Math.random() * 20) + 80}% success rate
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Enrollments</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">{commissions?.enrollments?.count || 0}</div>
          <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '600', marginTop: '8px' }}>
            Total enrollments
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Earned</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">
            KES {Number(commissions?.cumulativeTotal || 0).toFixed(2)}
          </div>
          <div style={{ fontSize: '13px', color: '#037b90', fontWeight: '600', marginTop: '8px' }}>
            Across all students
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="card">
        <div style={{ display: 'flex', gap: '24px', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px' }}>
          <button
            className={`btn ${activeTab === 'admissions' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => { setActiveTab('admissions'); setCurrentPage(1); }}
          >
            Admissions
          </button>
          <button
            className={`btn ${activeTab === 'enrollments' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => { setActiveTab('enrollments'); setCurrentPage(1); }}
          >
            Enrollments
          </button>
        </div>

        {activeTab === 'admissions' && (
          <div>
            <div className="card-header" style={{ marginBottom: '24px', paddingBottom: '16px' }}>
              <div>
                <h2 className="card-title">Student Admissions</h2>
                <p className="card-subtitle">Students who have been successfully admitted to OUK programs</p>
              </div>
              <button className="btn btn-outline btn-sm">📥 Export List</button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student ID</th>
                    <th>Admission Date</th>
                    <th>Program</th>
                    <th>Status</th>
                    <th>Commission</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((admission, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${index % 2 === 0 ? '#3b82f6' : '#10b981'}, ${index % 2 === 0 ? '#8b5cf6' : '#f59e0b'})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '12px'
                          }}>
                            {(admission.student_id || '?').toString().slice(-2)}
                          </div>
                          <div>
                            <span className="badge badge-info" style={{ fontSize: '12px', fontWeight: '700' }}>
                              {admission.student_id || 'Unknown'}
                            </span>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Student ID</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>
                            {new Date(admission.date).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            📅 {new Date(admission.date).toLocaleDateString('en-US', { weekday: 'long' })}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>{admission.programme || 'N/A'}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>🎓 Degree Programme</div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${admission.verified ? 'badge-success' : 'badge-warning'}`}>
                          {admission.verified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                            KES {Number(admission.commission_amount || 0).toFixed(2)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            KES 500 admission bonus
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleViewStudent(admission.student_id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <span>👁</span>
                          View Student
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                pagination={pagination}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                loading={loading}
              />
            </div>
          </div>
        )}

        {activeTab === 'enrollments' && (
          <div>
            <div className="card-header" style={{ marginBottom: '24px', paddingBottom: '16px' }}>
              <div>
                <h2 className="card-title">Student Enrollments</h2>
                <p className="card-subtitle">Students who have enrolled and started their academic programs</p>
              </div>
              <button className="btn btn-outline btn-sm">📥 Export List</button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student ID</th>
                    <th>Enrollment Date</th>
                    <th>Program</th>
                    <th>Status</th>
                    <th>Commission</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((enrollment, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${index % 2 === 0 ? '#10b981' : '#f59e0b'}, ${index % 2 === 0 ? '#3b82f6' : '#ec4899'})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '12px'
                          }}>
                            {(enrollment.student_id || '?').toString().slice(-2)}
                          </div>
                          <div>
                            <span className="badge badge-info" style={{ fontSize: '12px', fontWeight: '700' }}>
                              {enrollment.student_id || 'Unknown'}
                            </span>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Student ID</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>
                            {new Date(enrollment.date).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            📅 Started classes
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>{enrollment.programme || 'N/A'}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>🎓 Degree Programme</div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${enrollment.verified ? 'badge-success' : 'badge-warning'}`}>
                          {enrollment.verified ? '✅ Active' : '⏳ Processing'}
                        </span>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '700', color: '#10b981', fontSize: '16px' }}>
                            KES {Number(enrollment.commission_amount || 0).toFixed(2)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            KES 300 registration bonus
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleViewStudent(enrollment.student_id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <span>👁</span>
                          View Student
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                pagination={pagination}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Student Details Modal */}
        {showStudentModal && selectedStudent && (
          <div className="modal-overlay" onClick={() => setShowStudentModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Student Details</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowStudentModal(false)}
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="student-detail-card">
                  <div className="student-avatar">
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6, #10b981)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: '700'
                    }}>
                      {selectedStudent.id.slice(-2)}
                    </div>
                  </div>

                  <div className="student-info">
                    <div className="student-detail-row">
                      <label className="detail-label">Student ID</label>
                      <span className="detail-value badge badge-info">{selectedStudent.id}</span>
                    </div>

                    <div className="student-detail-row">
                      <label className="detail-label">Name</label>
                      <span className="detail-value">{selectedStudent.name}</span>
                    </div>

                    <div className="student-detail-row">
                      <label className="detail-label">Program</label>
                      <span className="detail-value">{selectedStudent.program}</span>
                    </div>

                    <div className="student-detail-row">
                      <label className="detail-label">Email</label>
                      <span className="detail-value">{selectedStudent.email}</span>
                    </div>

                    <div className="student-detail-row phone-row">
                      <label className="detail-label">Phone Number</label>
                      <div className="phone-container">
                        <span className="detail-value phone-number">{selectedStudent.phone}</span>
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(selectedStudent.phone)}
                          title="Copy phone number"
                        >
                          <span>📋</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-outline"
                  onClick={() => setShowStudentModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Payments() {
  const [payments, setPayments] = useState({
    admissions: { total: 0, count: 0 },
    enrollments: { total: 0, count: 0 },
    cumulativeTotal: 0,
    payments: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 10, page: 1 });

  useEffect(() => {
    loadPayments();
  }, [currentPage]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getCommissions(currentPage, pagination.limit);
      setPayments(response.data);
      setPagination(response.data.pagination || { total: 0, limit: 10, page: 1 });
    } catch (error) {
      toast.error('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const totalEarned = payments.cumulativeTotal || 0;
  const totalPaid = (payments.payments || []).reduce((sum, p) => p.payment_status === 'paid' ? sum + parseFloat(p.cumulative_amount) : sum, 0);
  const pendingAmount = totalEarned - totalPaid;



  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            💰
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Payment Information...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching your commission and payment history</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content" style={{ position: 'relative', minHeight: '800px' }}>
      <style>
        {`
          @keyframes pulse-amber {
            0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
            100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
          }
        `}
      </style>

      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '24px',
        margin: '16px'
      }}>
        <div style={{
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '48px',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          maxWidth: '440px',
          transform: 'translateY(-40px)'
        }}>
          <div style={{
            width: '80px', height: '80px',
            background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
            borderRadius: '24px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 10px 25px -5px rgba(203, 213, 225, 0.5)',
            transform: 'rotate(-5deg)'
          }}>
            <span style={{ fontSize: '32px', transform: 'rotate(5deg)', display: 'block' }}>🛠️</span>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#037b90', marginBottom: '12px', letterSpacing: '-0.5px' }}>
            Under Maintenance
          </h2>
          <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.6', marginBottom: '32px' }}>
            We're currently upgrading the Payments architecture. This module will be back online shortly with enhanced capabilities.
          </p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '8px 16px', background: '#fffbeb', border: '1px solid #fef3c7',
            borderRadius: '20px', color: '#92400e', fontSize: '13px', fontWeight: '600'
          }}>
            <div style={{
              width: '8px', height: '8px', background: '#f59e0b',
              borderRadius: '50%', animation: 'pulse-amber 2s infinite'
            }}></div>
            Engineer at work
          </div>
        </div>
      </div>

      <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">My Payments</h1>
            <p className="page-subtitle">Track your commission earnings, payment history, and pending amounts</p>
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <button className="btn btn-outline btn-sm">Download Statement</button>
            <button className="btn btn-secondary btn-sm">Payment Settings</button>
          </div>
        </div>

        {/* Payment Summary Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-title">Total Earned</div>
              <div className="stat-card-icon" style={{ background: 'rgba(3, 123, 144, 0.1)', color: '#037b90' }}>💰</div>
            </div>
            <div className="stat-card-value">KES {totalEarned.toLocaleString()}</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
              Lifetime commission earnings
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-title">Amount Paid</div>
              <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>✅</div>
            </div>
            <div className="stat-card-value">KES {totalPaid.toLocaleString()}</div>
            <div className="stat-card-change positive">
              {totalPaid > 0 ? Math.round((totalPaid / totalEarned) * 100) : 0}% of total earned
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-title">Pending Payment</div>
              <div className="stat-card-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>⏳</div>
            </div>
            <div className="stat-card-value">KES {pendingAmount.toLocaleString()}</div>
            <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginTop: '8px' }}>
              Next payout: April 30, 2024
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-title">Payment Rate</div>
              <div className="stat-card-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>📊</div>
            </div>
            <div className="stat-card-value">{totalPaid > 0 ? Math.round((totalPaid / totalEarned) * 100) : 0}%</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
              Of earned commissions
            </div>
          </div>
        </div>

        {/* Commission Breakdown */}
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">Commission Breakdown</h2>
              <p className="card-subtitle">Detailed breakdown of your earnings by commission type</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))',
              borderRadius: '12px',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: '#3b82f6', fontSize: '16px' }}>Admission Commissions</span>
                <span style={{ fontSize: '24px' }}>🎓</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6', marginBottom: '8px' }}>
                KES {(payments.admissions?.total || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                {payments.admissions?.count || 0} students × KES 500
              </div>
            </div>

            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))',
              borderRadius: '12px',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: '#10b981', fontSize: '16px' }}>Registration Commissions</span>
                <span style={{ fontSize: '24px' }}>📚</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981', marginBottom: '8px' }}>
                KES {(payments.enrollments?.total || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                {payments.enrollments?.count || 0} registrations × KES 300
              </div>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">Payment Information</h2>
              <p className="card-subtitle">Important details about commission payments and schedule</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            <div style={{
              padding: '20px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                Payment Schedule
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                  <strong>Monthly Payments:</strong> Last day of each month
                </li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                  <strong>Processing Time:</strong> 3-5 business days
                </li>
                <li style={{ padding: '8px 0', fontSize: '14px' }}>
                  <strong>Payment Method:</strong> M-Pesa or Bank Transfer
                </li>
              </ul>
            </div>

            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(3, 123, 144, 0.1), rgba(3, 123, 144, 0.05))',
              borderRadius: '12px',
              border: '1px solid rgba(3, 123, 144, 0.2)'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '12px' }}>
                Commission Rates
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(3, 123, 144, 0.1)', fontSize: '14px' }}>
                  <strong>Student Admission:</strong> KES 500
                </li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(3, 123, 144, 0.1)', fontSize: '14px' }}>
                  <strong>Session Registration:</strong> KES 300
                </li>
                <li style={{ padding: '8px 0', fontSize: '14px' }}>
                  <strong>Maximum per Student:</strong> KES 800
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileStats, setProfileStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [emailValidation, setEmailValidation] = useState({ isValid: true, errors: [] });
  const [formData, setFormData] = useState({});
  const [show2FASettings, setShow2FASettings] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  // Nationality dropdown state
  const [nationalitySearch, setNationalitySearch] = useState('');
  const [showNationalityDropdown, setShowNationalityDropdown] = useState(false);

  // Countries data with flags
  const countries = [
    { code: 'AF', name: 'Afghan', flag: '🇦🇫' },
    { code: 'AL', name: 'Albanian', flag: '🇦🇱' },
    { code: 'DZ', name: 'Algerian', flag: '🇩🇿' },
    { code: 'US', name: 'American', flag: '🇺🇸' },
    { code: 'AD', name: 'Andorran', flag: '🇦🇩' },
    { code: 'AO', name: 'Angolan', flag: '🇦🇴' },
    { code: 'AR', name: 'Argentine', flag: '🇦🇷' },
    { code: 'AM', name: 'Armenian', flag: '🇦🇲' },
    { code: 'AU', name: 'Australian', flag: '🇦🇺' },
    { code: 'AT', name: 'Austrian', flag: '🇦🇹' },
    { code: 'AZ', name: 'Azerbaijani', flag: '🇦🇿' },
    { code: 'BS', name: 'Bahamian', flag: '🇧🇸' },
    { code: 'BH', name: 'Bahraini', flag: '🇧🇭' },
    { code: 'BD', name: 'Bangladeshi', flag: '🇧🇩' },
    { code: 'BB', name: 'Barbadian', flag: '🇧🇧' },
    { code: 'BY', name: 'Belarusian', flag: '🇧🇾' },
    { code: 'BE', name: 'Belgian', flag: '🇧🇪' },
    { code: 'BZ', name: 'Belizean', flag: '🇧🇿' },
    { code: 'BJ', name: 'Beninese', flag: '🇧🇯' },
    { code: 'BT', name: 'Bhutanese', flag: '🇧🇹' },
    { code: 'BO', name: 'Bolivian', flag: '🇧🇴' },
    { code: 'BA', name: 'Bosnian', flag: '🇧🇦' },
    { code: 'BW', name: 'Botswanan', flag: '🇧🇼' },
    { code: 'BR', name: 'Brazilian', flag: '🇧🇷' },
    { code: 'GB', name: 'British', flag: '🇬🇧' },
    { code: 'BN', name: 'Bruneian', flag: '🇧🇳' },
    { code: 'BG', name: 'Bulgarian', flag: '🇧🇬' },
    { code: 'BF', name: 'Burkinabe', flag: '🇧🇫' },
    { code: 'BI', name: 'Burundian', flag: '🇧🇮' },
    { code: 'KH', name: 'Cambodian', flag: '🇰🇭' },
    { code: 'CM', name: 'Cameroonian', flag: '🇨🇲' },
    { code: 'CA', name: 'Canadian', flag: '🇨🇦' },
    { code: 'CV', name: 'Cape Verdean', flag: '🇨🇻' },
    { code: 'CF', name: 'Central African', flag: '🇨🇫' },
    { code: 'TD', name: 'Chadian', flag: '🇹🇩' },
    { code: 'CL', name: 'Chilean', flag: '🇨🇱' },
    { code: 'CN', name: 'Chinese', flag: '🇨🇳' },
    { code: 'CO', name: 'Colombian', flag: '🇨🇴' },
    { code: 'KM', name: 'Comoran', flag: '🇰🇲' },
    { code: 'CG', name: 'Congolese', flag: '🇨🇬' },
    { code: 'CR', name: 'Costa Rican', flag: '🇨🇷' },
    { code: 'HR', name: 'Croatian', flag: '🇭🇷' },
    { code: 'CU', name: 'Cuban', flag: '🇨🇺' },
    { code: 'CY', name: 'Cypriot', flag: '🇨🇾' },
    { code: 'CZ', name: 'Czech', flag: '🇨🇿' },
    { code: 'DK', name: 'Danish', flag: '🇩🇰' },
    { code: 'DJ', name: 'Djiboutian', flag: '🇩🇯' },
    { code: 'DO', name: 'Dominican', flag: '🇩🇴' },
    { code: 'NL', name: 'Dutch', flag: '🇳🇱' },
    { code: 'EC', name: 'Ecuadorian', flag: '🇪🇨' },
    { code: 'EG', name: 'Egyptian', flag: '🇪🇬' },
    { code: 'SV', name: 'Salvadoran', flag: '🇸🇻' },
    { code: 'GQ', name: 'Equatorial Guinean', flag: '🇬🇶' },
    { code: 'ER', name: 'Eritrean', flag: '🇪🇷' },
    { code: 'EE', name: 'Estonian', flag: '🇪🇪' },
    { code: 'SZ', name: 'Eswatini', flag: '🇸🇿' },
    { code: 'ET', name: 'Ethiopian', flag: '🇪🇹' },
    { code: 'FJ', name: 'Fijian', flag: '🇫🇯' },
    { code: 'FI', name: 'Finnish', flag: '🇫🇮' },
    { code: 'FR', name: 'French', flag: '🇫🇷' },
    { code: 'GA', name: 'Gabonese', flag: '🇬🇦' },
    { code: 'GM', name: 'Gambian', flag: '🇬🇲' },
    { code: 'GE', name: 'Georgian', flag: '🇬🇪' },
    { code: 'DE', name: 'German', flag: '🇩🇪' },
    { code: 'GH', name: 'Ghanaian', flag: '🇬🇭' },
    { code: 'GR', name: 'Greek', flag: '🇬🇷' },
    { code: 'GD', name: 'Grenadian', flag: '🇬🇩' },
    { code: 'GT', name: 'Guatemalan', flag: '🇬🇹' },
    { code: 'GN', name: 'Guinean', flag: '🇬🇳' },
    { code: 'GW', name: 'Guinea-Bissauan', flag: '🇬🇼' },
    { code: 'GY', name: 'Guyanese', flag: '🇬🇾' },
    { code: 'HT', name: 'Haitian', flag: '🇭🇹' },
    { code: 'HN', name: 'Honduran', flag: '🇭🇳' },
    { code: 'HU', name: 'Hungarian', flag: '🇭🇺' },
    { code: 'IS', name: 'Icelandic', flag: '🇮🇸' },
    { code: 'IN', name: 'Indian', flag: '🇮🇳' },
    { code: 'ID', name: 'Indonesian', flag: '🇮🇩' },
    { code: 'IR', name: 'Iranian', flag: '🇮🇷' },
    { code: 'IQ', name: 'Iraqi', flag: '🇮🇶' },
    { code: 'IE', name: 'Irish', flag: '🇮🇪' },
    { code: 'IL', name: 'Israeli', flag: '🇮🇱' },
    { code: 'IT', name: 'Italian', flag: '🇮🇹' },
    { code: 'CI', name: 'Ivorian', flag: '🇨🇮' },
    { code: 'JM', name: 'Jamaican', flag: '🇯🇲' },
    { code: 'JP', name: 'Japanese', flag: '🇯🇵' },
    { code: 'JO', name: 'Jordanian', flag: '🇯🇴' },
    { code: 'KZ', name: 'Kazakhstani', flag: '🇰🇿' },
    { code: 'KE', name: 'Kenyan', flag: '🇰🇪' },
    { code: 'KI', name: 'Kiribati', flag: '🇰🇮' },
    { code: 'KP', name: 'North Korean', flag: '🇰🇵' },
    { code: 'KR', name: 'South Korean', flag: '🇰🇷' },
    { code: 'KW', name: 'Kuwaiti', flag: '🇰🇼' },
    { code: 'KG', name: 'Kyrgyzstani', flag: '🇰🇬' },
    { code: 'LA', name: 'Laotian', flag: '🇱🇦' },
    { code: 'LV', name: 'Latvian', flag: '🇱🇻' },
    { code: 'LB', name: 'Lebanese', flag: '🇱🇧' },
    { code: 'LS', name: 'Lesotho', flag: '🇱🇸' },
    { code: 'LR', name: 'Liberian', flag: '🇱🇷' },
    { code: 'LY', name: 'Libyan', flag: '🇱🇾' },
    { code: 'LI', name: 'Liechtensteiner', flag: '🇱🇮' },
    { code: 'LT', name: 'Lithuanian', flag: '🇱🇹' },
    { code: 'LU', name: 'Luxembourgish', flag: '🇱🇺' },
    { code: 'MK', name: 'Macedonian', flag: '🇲🇰' },
    { code: 'MG', name: 'Malagasy', flag: '🇲🇬' },
    { code: 'MW', name: 'Malawian', flag: '🇲🇼' },
    { code: 'MY', name: 'Malaysian', flag: '🇲🇾' },
    { code: 'MV', name: 'Maldivian', flag: '🇲🇻' },
    { code: 'ML', name: 'Malian', flag: '🇲🇱' },
    { code: 'MT', name: 'Maltese', flag: '🇲🇹' },
    { code: 'MH', name: 'Marshallese', flag: '🇲🇭' },
    { code: 'MR', name: 'Mauritanian', flag: '🇲🇷' },
    { code: 'MU', name: 'Mauritian', flag: '🇲🇺' },
    { code: 'MX', name: 'Mexican', flag: '🇲🇽' },
    { code: 'FM', name: 'Micronesian', flag: '🇫🇲' },
    { code: 'MD', name: 'Moldovan', flag: '🇲🇩' },
    { code: 'MC', name: 'Monacan', flag: '🇲🇨' },
    { code: 'MN', name: 'Mongolian', flag: '🇲🇳' },
    { code: 'ME', name: 'Montenegrin', flag: '🇲🇪' },
    { code: 'MA', name: 'Moroccan', flag: '🇲🇦' },
    { code: 'MZ', name: 'Mozambican', flag: '🇲🇿' },
    { code: 'MM', name: 'Burmese', flag: '🇲🇲' },
    { code: 'NA', name: 'Namibian', flag: '🇳🇦' },
    { code: 'NR', name: 'Nauruan', flag: '🇳🇷' },
    { code: 'NP', name: 'Nepalese', flag: '🇳🇵' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
    { code: 'NI', name: 'Nicaraguan', flag: '🇳🇮' },
    { code: 'NE', name: 'Nigerien', flag: '🇳🇪' },
    { code: 'NG', name: 'Nigerian', flag: '🇳🇬' },
    { code: 'NO', name: 'Norwegian', flag: '🇳🇴' },
    { code: 'OM', name: 'Omani', flag: '🇴🇲' },
    { code: 'PK', name: 'Pakistani', flag: '🇵🇰' },
    { code: 'PW', name: 'Palauan', flag: '🇵🇼' },
    { code: 'PS', name: 'Palestinian', flag: '🇵🇸' },
    { code: 'PA', name: 'Panamanian', flag: '🇵🇦' },
    { code: 'PG', name: 'Papua New Guinean', flag: '🇵🇬' },
    { code: 'PY', name: 'Paraguayan', flag: '🇵🇾' },
    { code: 'PE', name: 'Peruvian', flag: '🇵🇪' },
    { code: 'PH', name: 'Filipino', flag: '🇵🇭' },
    { code: 'PL', name: 'Polish', flag: '🇵🇱' },
    { code: 'PT', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'QA', name: 'Qatari', flag: '🇶🇦' },
    { code: 'RO', name: 'Romanian', flag: '🇷🇴' },
    { code: 'RU', name: 'Russian', flag: '🇷🇺' },
    { code: 'RW', name: 'Rwandan', flag: '🇷🇼' },
    { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳' },
    { code: 'LC', name: 'Saint Lucian', flag: '🇱🇨' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨' },
    { code: 'WS', name: 'Samoan', flag: '🇼🇸' },
    { code: 'SM', name: 'San Marinese', flag: '🇸🇲' },
    { code: 'ST', name: 'Sao Tomean', flag: '🇸🇹' },
    { code: 'SA', name: 'Saudi Arabian', flag: '🇸🇦' },
    { code: 'SN', name: 'Senegalese', flag: '🇸🇳' },
    { code: 'RS', name: 'Serbian', flag: '🇷🇸' },
    { code: 'SC', name: 'Seychellois', flag: '🇸🇨' },
    { code: 'SL', name: 'Sierra Leonean', flag: '🇸🇱' },
    { code: 'SG', name: 'Singaporean', flag: '🇸🇬' },
    { code: 'SK', name: 'Slovak', flag: '🇸🇰' },
    { code: 'SI', name: 'Slovenian', flag: '🇸🇮' },
    { code: 'SB', name: 'Solomon Islander', flag: '🇸🇧' },
    { code: 'SO', name: 'Somali', flag: '🇸🇴' },
    { code: 'ZA', name: 'South African', flag: '🇿🇦' },
    { code: 'SS', name: 'South Sudanese', flag: '🇸🇸' },
    { code: 'ES', name: 'Spanish', flag: '🇪🇸' },
    { code: 'LK', name: 'Sri Lankan', flag: '🇱🇰' },
    { code: 'SD', name: 'Sudanese', flag: '🇸🇩' },
    { code: 'SR', name: 'Surinamese', flag: '🇸🇷' },
    { code: 'SE', name: 'Swedish', flag: '🇸🇪' },
    { code: 'CH', name: 'Swiss', flag: '🇨🇭' },
    { code: 'SY', name: 'Syrian', flag: '🇸🇾' },
    { code: 'TW', name: 'Taiwanese', flag: '🇹🇼' },
    { code: 'TJ', name: 'Tajikistani', flag: '🇹🇯' },
    { code: 'TZ', name: 'Tanzanian', flag: '🇹🇿' },
    { code: 'TH', name: 'Thai', flag: '🇹🇭' },
    { code: 'TL', name: 'Timorese', flag: '🇹🇱' },
    { code: 'TG', name: 'Togolese', flag: '🇹🇬' },
    { code: 'TO', name: 'Tongan', flag: '🇹🇴' },
    { code: 'TT', name: 'Trinidadian', flag: '🇹🇹' },
    { code: 'TN', name: 'Tunisian', flag: '🇹🇳' },
    { code: 'TR', name: 'Turkish', flag: '🇹🇷' },
    { code: 'TM', name: 'Turkmen', flag: '🇹🇲' },
    { code: 'TV', name: 'Tuvaluan', flag: '🇹🇻' },
    { code: 'UG', name: 'Ugandan', flag: '🇺🇬' },
    { code: 'UA', name: 'Ukrainian', flag: '🇺🇦' },
    { code: 'AE', name: 'Emirati', flag: '🇦🇪' },
    { code: 'UY', name: 'Uruguayan', flag: '🇺🇾' },
    { code: 'UZ', name: 'Uzbekistani', flag: '🇺🇿' },
    { code: 'VU', name: 'Vanuatuan', flag: '🇻🇺' },
    { code: 'VA', name: 'Vatican', flag: '🇻🇦' },
    { code: 'VE', name: 'Venezuelan', flag: '🇻🇪' },
    { code: 'VN', name: 'Vietnamese', flag: '🇻🇳' },
    { code: 'YE', name: 'Yemeni', flag: '🇾🇪' },
    { code: 'ZM', name: 'Zambian', flag: '🇿🇲' },
    { code: 'ZW', name: 'Zimbabwean', flag: '🇿🇼' }
  ];

  // Filter countries based on search
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(nationalitySearch.toLowerCase())
  );

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      setFormData(profile);
      setNationalitySearch(profile.nationality || '');
    }
  }, [profile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nationality-dropdown')) {
        setShowNationalityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadProfile = async () => {
    try {
      const [profileResponse, statsResponse] = await Promise.all([
        recruiterAPI.getProfile(),
        studentAPI.getProfileStats()
      ]);
      setProfile(profileResponse.data);
      setProfileStats(statsResponse.data);
    } catch (error) {
      console.error('Profile loading error:', error);
      toast.error('Failed to load profile: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailValidationChange = (validation) => {
    setEmailValidation(validation);
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNationalitySelect = (nationality) => {
    setFormData(prev => ({
      ...prev,
      nationality: nationality
    }));
    setNationalitySearch(nationality);
    setShowNationalityDropdown(false);
  };

  const handleSaveProfile = async () => {
    try {
      await recruiterAPI.updateProfile(formData);
      toast.success('Profile updated successfully!');
      setProfile(formData);
      setEditing(false);
    } catch (error) {
      console.error('Profile update error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update profile';
      toast.error('Failed to update profile: ' + errorMessage);
    }
  };

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            👤
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Your Profile...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching your personal information and settings</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content profile-responsive">
      <style>{`
        .profile-responsive .page-header { display: flex; justify-content: space-between; align-items: flex-start; }
        .profile-responsive .profile-avatar { width: 120px; height: 120px; min-width: 120px; }
        .profile-responsive .profile-header-inner { display: flex; align-items: center; gap: 32px; margin-bottom: 32px; }
        .profile-responsive .profile-badges { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
        .profile-responsive .profile-stats { display: flex; gap: 32px; flex-wrap: wrap; }
        .profile-responsive .info-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
        .profile-responsive .info-grid-1 { display: grid; grid-template-columns: 1fr; gap: 24px; }
        .profile-responsive .payment-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
        .profile-responsive .action-buttons { display: flex; gap: 16px; margin-top: 32px; flex-wrap: wrap; }
        .profile-responsive .page-header-buttons { display: flex; gap: 16px; }
        .profile-responsive .security-tip { display: flex; align-items: center; gap: 12px; }

        @media (max-width: 768px) {
          .profile-responsive .page-header { flex-direction: column; gap: 16px; }
          .profile-responsive .page-header-buttons { width: 100%; }
          .profile-responsive .page-header-buttons .btn { flex: 1; text-align: center; }
          .profile-responsive .profile-header-inner { flex-direction: column; text-align: center; }
          .profile-responsive .profile-badges { justify-content: center; }
          .profile-responsive .profile-stats { justify-content: center; }
          .profile-responsive .profile-avatar { width: 80px; height: 80px; min-width: 80px; font-size: 32px !important; }
          .profile-responsive .profile-header-inner h2 { font-size: 20px; }
          .profile-responsive .info-grid-2 { grid-template-columns: 1fr; }
          .profile-responsive .payment-grid { grid-template-columns: 1fr; }
          .profile-responsive .form-grid { grid-template-columns: 1fr !important; }
          .profile-responsive .security-tip { flex-direction: column; text-align: center; }
        }

        @media (max-width: 480px) {
          .profile-responsive .page-header-buttons { flex-direction: column; }
          .profile-responsive .profile-badges { flex-direction: column; align-items: stretch; }
          .profile-responsive .profile-badges .badge,
          .profile-responsive .profile-badges .btn { text-align: center; }
          .profile-responsive .profile-stats { flex-direction: column; align-items: center; gap: 16px; }
          .profile-responsive .profile-stats > div { text-align: center; }
        }
      `}</style>
      <div className="page-header">
        <div>
          <h1 className="page-title">👤 My Profile</h1>
          <p className="page-subtitle">Manage your personal information, settings, and account preferences</p>
        </div>
        <div className="page-header-buttons">
          <button className="btn btn-outline" onClick={handleChangePassword}>🔒 Change Password</button>
          <button
            className="btn btn-primary"
            onClick={() => setEditing(!editing)}
          >
            {editing ? '❌ Cancel' : '✏️ Edit Profile'}
          </button>
        </div>
      </div>

      {/* Profile Header Card */}
      <div className="card">
        <div className="profile-header-inner">
          <div className="profile-avatar" style={{
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '48px',
            fontWeight: '700',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
            aspectRatio: '1'
          }}>
            {profile?.full_name?.split(' ').map(n => n[0]).join('') || '??'}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
              {profile?.full_name}
            </h2>
            <div className="profile-badges">
              <span className="badge badge-info" style={{ fontSize: '14px', padding: '8px 16px' }}>
                🔗 {profile?.referral_code}
              </span>
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setShowQRModal(true)}
                style={{ fontSize: '13px', padding: '6px 14px' }}
              >
                📱 Share Verification
              </button>
              <span className="badge badge-success" style={{ fontSize: '14px', padding: '8px 16px' }}>
                🟢 {profile?.status}
              </span>
            </div>
            <div className="profile-stats">
              <div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                  {profileStats?.studentsRecruited || 0}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Students Recruited</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                  KES {Number(profileStats?.totalEarnings || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Total Earnings</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                  #{profileStats?.ranking || '-'}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Ranking</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">📋 Personal Information</h2>
              <p className="card-subtitle">Your basic details and contact information</p>
            </div>
          </div>
          {editing ? (
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">👤 Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  className="form-input"
                  value={formData.full_name || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">📧 Email Address</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email || ''}
                  disabled={true}
                  style={{
                    backgroundColor: '#f8fafc',
                    color: '#64748b',
                    cursor: 'not-allowed'
                  }}
                  title="Email cannot be changed as it's your unique identifier"
                />
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Email cannot be changed as it's your unique login identifier
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">📱 Mobile Number</label>
                <input
                  type="tel"
                  name="mobile_number"
                  className="form-input"
                  value={formData.mobile_number || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">🌍 Nationality</label>
                <div style={{ position: 'relative' }} className="nationality-dropdown">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="🔍 Search nationality..."
                    value={nationalitySearch}
                    onChange={(e) => {
                      setNationalitySearch(e.target.value);
                      setShowNationalityDropdown(true);
                    }}
                    onFocus={() => setShowNationalityDropdown(true)}
                  />
                  {showNationalityDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000
                    }}>
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country) => (
                          <div
                            key={country.code}
                            onClick={() => handleNationalitySelect(country.name)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <span style={{ fontSize: '16px' }}>{country.flag}</span>
                            <span>{country.name}</span>
                          </div>
                        ))
                      ) : (
                        <div style={{
                          padding: '12px 16px',
                          color: '#64748b',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}>
                          No countries found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">🆔 ID/Passport Number</label>
                <input
                  type="text"
                  name="id_passport"
                  className="form-input"
                  value={formData.id_passport || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">🏛️ KRA PIN</label>
                <input
                  type="text"
                  name="kra_pin"
                  className="form-input"
                  value={formData.kra_pin || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          ) : (
            <div className="info-grid-2">
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>👤 Full Name</div>
                  <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.full_name}</div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📧 Email</div>
                  <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.email}</div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📱 Mobile</div>
                  <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.mobile_number}</div>
                </div>
              </div>
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🌍 Nationality</div>
                  <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.nationality}</div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🆔 ID/Passport</div>
                  <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.id_passport}</div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏛️ KRA PIN</div>
                  <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.kra_pin}</div>
                </div>
              </div>
            </div>
          )}
          {editing && (
            <div className="action-buttons">
              <button className="btn btn-primary" onClick={handleSaveProfile}>✅ Save Changes</button>
              <button className="btn btn-outline" onClick={() => setEditing(false)}>❌ Cancel</button>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">⚙️ Account Settings</h2>
              <p className="card-subtitle">Security and notification preferences</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <button className="btn btn-outline" style={{ justifyContent: 'flex-start', padding: '16px 20px' }} onClick={handleChangePassword}>
              <span style={{ marginRight: '12px', fontSize: '18px' }}>🔒</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>Change Password</div>
              </div>
            </button>
            <button className="btn btn-outline" style={{ justifyContent: 'flex-start', padding: '16px 20px' }} onClick={() => setShow2FASettings(true)}>
              <span style={{ marginRight: '12px', fontSize: '18px' }}>🛡️</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>Two-Factor Authentication</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">📍 Location Information</h2>
            <p className="card-subtitle">Your geographical and address details</p>
          </div>
        </div>
        {editing ? (
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">🏛️ County</label>
              <input
                type="text"
                name="county"
                className="form-input"
                value={formData.county || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">🏘️ Subcounty</label>
              <input
                type="text"
                name="subcounty"
                className="form-input"
                value={formData.subcounty || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">📍 Location</label>
              <input
                type="text"
                name="location"
                className="form-input"
                value={formData.location || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">🏠 Sublocation</label>
              <input
                type="text"
                name="sublocation"
                className="form-input"
                value={formData.sublocation || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">🏠 Village</label>
              <input
                type="text"
                name="village"
                className="form-input"
                value={formData.village || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">🏠 Residential Address</label>
              <textarea
                name="residential_address"
                className="form-input"
                value={formData.residential_address || ''}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label className="form-label">📮 Postal Address</label>
              <input
                type="text"
                name="postal_address"
                className="form-input"
                value={formData.postal_address || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
        ) : (
          <div className="info-grid-2">
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏛️ County</div>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '16px' }}>{profile?.county}</div>

              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏘️ Subcounty</div>
              <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.subcounty}</div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📍 Location</div>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '16px' }}>{profile?.location}</div>

              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏠 Sublocation</div>
              <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.sublocation}</div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏠 Village</div>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '16px' }}>{profile?.village}</div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏠 Residential Address</div>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '16px' }}>{profile?.residential_address}</div>

              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📮 Postal Address</div>
              <div style={{ fontWeight: '600', fontSize: '16px' }}>{profile?.postal_address}</div>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">💳 Payment Information</h2>
            <p className="card-subtitle">Your banking and mobile money details for commission payments</p>
          </div>
        </div>
        <div className="payment-grid">
          <div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏦 Bank Account Details</div>
            <div style={{
              fontWeight: '600',
              fontSize: '16px',
              fontFamily: 'monospace',
              background: '#f8fafc',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ fontSize: '14px', color: '#475569', marginBottom: '4px', fontFamily: 'inherit' }}>
                {profile?.bank_name || 'N/A'}{profile?.branch_code ? ` (Branch: ${profile?.branch_code})` : ''}
              </div>
              {profile?.bank_account || 'N/A'}
            </div>
          </div>
        </div>
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: 'linear-gradient(135deg, #dbeafe, #f3e8ff)',
          borderRadius: '12px',
          border: '1px solid #bfdbfe'
        }}>
          <div className="security-tip">
            <span style={{ fontSize: '24px' }}>💡</span>
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e40af' }}>Payment Security</div>
              <div style={{ fontSize: '13px', color: '#3730a3', marginTop: '4px' }}>
                Your payment information is encrypted and secure. Commissions are processed monthly on the 30th.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2FA Settings Modal */}
      {show2FASettings && (
        <TwoFactorSettings onClose={() => setShow2FASettings(false)} />
      )}

      {/* QR Verification Modal */}
      {showQRModal && profile && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Recruiter Verification QR</h3>
              <button className="modal-close" onClick={() => setShowQRModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '32px' }}>
              <QRCodeSVG value={`${window.location.origin}/verify?code=${encodeURIComponent(profile.referral_code)}`} size={200} />
              <p style={{ margin: '16px 0 4px', fontWeight: 600, fontSize: '16px', color: '#1e293b' }}>{profile.full_name}</p>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Code: {profile.referral_code}</p>
              <div style={{ marginTop: '16px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#166534' }}>
                  Share this QR code or verification link with students to prove you are a genuine OUK recruiter.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'center' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/verify?code=${encodeURIComponent(profile.referral_code)}`);
                    toast.success('Verification link copied!');
                  }}
                >
                  Copy Link
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: 'OUK Recruiter Verification',
                        text: `Verify I'm a genuine OUK recruiter: ${window.location.origin}/verify?code=${encodeURIComponent(profile.referral_code)}`
                      });
                    } else {
                      navigator.clipboard.writeText(`${window.location.origin}/verify?code=${encodeURIComponent(profile.referral_code)}`);
                      toast.success('Verification link copied!');
                    }
                  }}
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Leaderboard Component
function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 10, page: 1 });

  useEffect(() => {
    loadLeaderboard();
  }, [currentPage]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const [leaderboardResponse, profileResponse] = await Promise.all([
        studentAPI.getLeaderboard(currentPage, pagination.limit),
        recruiterAPI.getProfile()
      ]);
      setLeaderboard(leaderboardResponse.data.data || []);
      setPagination(leaderboardResponse.data.pagination || { total: 0, limit: 10, page: 1 });
      setCurrentUser(profileResponse.data);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            🏆
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Leaderboard...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching top performing recruiters</p>
        </div>
      </div>
    );
  }

  const currentUserRank = leaderboard.find(r => r.full_name === currentUser?.full_name);

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">🏆 Leaderboard</h1>
          <p className="page-subtitle">Top performing recruiters ranked by total earnings and student recruitment</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <button className="btn btn-outline btn-sm" onClick={loadLeaderboard}>🔄 Refresh</button>
          <button className="btn btn-secondary btn-sm">📊 View Analytics</button>
        </div>
      </div>

      {/* Current User Position Card */}
      {currentUserRank && (
        <div className="card" style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', border: '2px solid #0ea5e9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0ea5e9, #3b82f6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '32px',
              fontWeight: '700'
            }}>
              #{currentUserRank.rank}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: '#0c4a6e' }}>
                Your Current Position
              </h3>
              <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#0ea5e9' }}>
                    {currentUserRank.unique_student_count || 0} Students
                  </div>
                  <div style={{ fontSize: '12px', color: '#0c4a6e' }}>Total Recruited</div>
                </div>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>
                    KES {Number(currentUserRank.total_earnings || 0).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#0c4a6e' }}>Total Earnings</div>
                </div>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#f59e0b' }}>
                    {currentUserRank.referral_code}
                  </div>
                  <div style={{ fontSize: '12px', color: '#0c4a6e' }}>Referral Code</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top 3 Podium */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <div>
            <h2 className="card-title">🥇 Top Performers</h2>
            <p className="card-subtitle">The highest earning recruiters this period</p>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'end', gap: '24px', padding: '32px 0' }}>
          {/* Second Place */}
          {leaderboard[1] && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '100px',
                height: '120px',
                background: 'linear-gradient(135deg, #e5e7eb, #d1d5db)',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                position: 'relative'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🥈</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#6b7280' }}>#2</div>
                <div style={{
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#e5e7eb',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  SILVER
                </div>
              </div>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                {leaderboard[1].full_name || 'Unknown'}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>
                KES {Number(leaderboard[1].total_earnings).toFixed(2)}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {leaderboard[1]?.unique_student_count || 0} students
              </div>
            </div>
          )}

          {/* First Place */}
          {leaderboard[0] && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '120px',
                height: '150px',
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                position: 'relative',
                boxShadow: '0 10px 25px -5px rgb(245 158 11 / 0.4)'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>🥇</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: 'white' }}>#1</div>
                <div style={{
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#f59e0b',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  GOLD
                </div>
              </div>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                {leaderboard[0].full_name || 'Unknown'}
              </div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>
                KES {Number(leaderboard[0].total_earnings).toFixed(2)}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {leaderboard[0]?.unique_student_count || 0} students
              </div>
            </div>
          )}

          {/* Third Place */}
          {leaderboard[2] && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '100px',
                height: '100px',
                background: 'linear-gradient(135deg, #cd7f32, #a0522d)',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                position: 'relative'
              }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>🥉</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>#3</div>
                <div style={{
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#cd7f32',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  BRONZE
                </div>
              </div>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                {leaderboard[2].full_name || 'Unknown'}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>
                KES {Number(leaderboard[2].total_earnings).toFixed(2)}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {leaderboard[2]?.unique_student_count || 0} students
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Leaderboard Table */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">📊 Complete Rankings</h2>
            <p className="card-subtitle">All active recruiters ranked by performance</p>
          </div>
          <button className="btn btn-outline btn-sm">📥 Export Rankings</button>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Recruiter</th>
                <th>Location</th>
                <th>Students</th>
                <th>Admissions</th>
                <th>Enrollments</th>
                <th>Total Earnings</th>
                <th>Referral Code</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((recruiter, index) => (
                <tr
                  key={recruiter.id}
                  style={{
                    backgroundColor: recruiter.full_name === currentUser?.full_name ? '#f0f9ff' : 'transparent',
                    border: recruiter.full_name === currentUser?.full_name ? '2px solid #0ea5e9' : 'none'
                  }}
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: index < 3
                          ? (index === 0 ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                            : index === 1 ? 'linear-gradient(135deg, #e5e7eb, #d1d5db)'
                              : 'linear-gradient(135deg, #cd7f32, #a0522d)')
                          : 'linear-gradient(135deg, #64748b, #475569)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '14px'
                      }}>
                        #{recruiter.rank}
                      </div>
                      {index < 3 && (
                        <span style={{ fontSize: '20px' }}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${index % 2 === 0 ? '#3b82f6' : '#10b981'}, ${index % 2 === 0 ? '#8b5cf6' : '#f59e0b'})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '12px'
                      }}>
                        {(recruiter.full_name || '?').split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          {recruiter.full_name || 'Unknown'}
                          {recruiter.full_name && recruiter.full_name === currentUser?.full_name && (
                            <span style={{
                              marginLeft: '8px',
                              fontSize: '12px',
                              backgroundColor: '#0ea5e9',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              YOU
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Active Recruiter</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>{recruiter.county}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{recruiter.subcounty}</div>
                    </div>
                  </td>
                  <td>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#3b82f6' }}>
                        {recruiter.unique_student_count || 0}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>total</div>
                    </div>
                  </td>
                  <td>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>
                        {recruiter.admission_count}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>admitted</div>
                    </div>
                  </td>
                  <td>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>
                        {recruiter.enrollment_count}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>enrolled</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                        KES {Number(recruiter.total_earnings).toFixed(2)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        A: KES {Number(recruiter.admission_earnings).toFixed(2)} |
                        E: KES {Number(recruiter.enrollment_earnings).toFixed(2)}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info" style={{ fontSize: '12px', fontWeight: '700' }}>
                      {recruiter.referral_code}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default RecruiterDashboard;

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [archivedIds, setArchivedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 15, page: 1 });

  useEffect(() => {
    loadNotifications();
  }, [filter, currentPage]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: pagination.limit,
        is_archived: filter === 'archived'
      };

      if (filter !== 'all' && filter !== 'archived') {
        if (filter === 'unread') {
          params.is_read = false;
        } else {
          params.type = filter;
        }
      }

      const response = await notificationAPI.getAll(params);
      setNotifications(response.data.notifications || []);
      setPagination(response.data.pagination || { total: 0, limit: 15, page: 1 });

      const initialArchived = new Set((response.data.notifications || []).filter(n => n.is_archived).map(n => n.id));
      setArchivedIds(initialArchived);

      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      if (archivedIds.has(notificationId)) return; // Don't mark archived as read from here
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      toast.success('Notification marked as read');
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleArchive = async (id) => {
    try {
      await notificationAPI.archive(id);
      setArchivedIds(prev => new Set(prev).add(id));
      toast.success('Notification archived');
    } catch (e) {
      toast.error('Failed to archive notification');
    }
  };

  const handleRestore = async (id) => {
    try {
      await notificationAPI.restore(id);
      setArchivedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success('Notification restored');
    } catch (e) {
      toast.error('Failed to restore notification');
    }
  };

  const handleDelete = async (id) => {
    try {
      if (!window.confirm('Permanently delete this notification?')) return;
      await notificationAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification deleted permanently');
    } catch (e) {
      toast.error('Failed to delete notification');
    }
  };

  const toggleSelectAll = () => {
    if (selectedNotifications.size === notifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(notifications.map(n => n.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedNotifications(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkAction = async (action) => {
    if (selectedNotifications.size === 0) return;

    const ids = Array.from(selectedNotifications);
    try {
      await notificationAPI.bulkAction(ids, action, false);

      if (action === 'read') {
        setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - ids.length));
      } else if (action === 'delete') {
        setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
      } else if (action === 'archive') {
        setArchivedIds(prev => {
          const next = new Set(prev);
          ids.forEach(id => next.add(id));
          return next;
        });
      } else if (action === 'restore') {
        setArchivedIds(prev => {
          const next = new Set(prev);
          ids.forEach(id => next.delete(id));
          return next;
        });
      }

      setSelectedNotifications(new Set());
      toast.success(`Bulk action ${action} completed`);
    } catch (e) {
      toast.error('Bulk action failed');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;

    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'admission': return '🎓';
      case 'enrollment': return '📚';
      case 'payment': return '💰';
      case 'achievement': return '🏆';
      case 'system': return '⚙️';
      default: return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'admission': return '#3b82f6';
      case 'enrollment': return '#10b981';
      case 'payment': return '#037b90';
      case 'achievement': return '#f59e0b';
      case 'system': return '#64748b';
      default: return '#64748b';
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            🔔
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Notifications...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching your recent activity and updates</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-subtitle">Stay updated with your recruitment activities and important announcements</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          {unreadCount > 0 && (
            <button
              className="btn btn-outline btn-sm"
              onClick={handleMarkAllAsRead}
            >
              Mark All Read ({unreadCount})
            </button>
          )}
          <button className="btn btn-secondary btn-sm">Settings</button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="card">
        <div style={{ display: 'flex', gap: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All', count: notifications.filter(n => !archivedIds.has(n.id)).length },
            { key: 'unread', label: 'Unread', count: unreadCount },
            { key: 'admission', label: 'Admissions', count: notifications.filter(n => n.type === 'admission' && !archivedIds.has(n.id)).length },
            { key: 'enrollment', label: 'Enrollments', count: notifications.filter(n => n.type === 'enrollment' && !archivedIds.has(n.id)).length },
            { key: 'payment', label: 'Payments', count: notifications.filter(n => n.type === 'payment' && !archivedIds.has(n.id)).length },
            { key: 'achievement', label: 'Achievements', count: notifications.filter(n => n.type === 'achievement' && !archivedIds.has(n.id)).length },
            { key: 'system', label: 'System', count: notifications.filter(n => n.type === 'system' && !archivedIds.has(n.id)).length },
            { key: 'archived', label: 'Archived', count: archivedIds.size }
          ].map(tab => (
            <button
              key={tab.key}
              className={`btn ${filter === tab.key ? 'btn-primary' : 'btn-outline'} btn-sm`}
              onClick={() => {
                setFilter(tab.key);
                setSelectedNotifications(new Set());
              }}
              style={{ minWidth: 'auto' }}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Bulk Action Bar */}
        {selectedNotifications.size > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            marginBottom: '24px',
            border: '1px solid #e2e8f0',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="checkbox"
                checked={selectedNotifications.size === notifications.length}
                onChange={toggleSelectAll}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: '600', color: '#475569' }}>
                {selectedNotifications.size} selected
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-outline btn-sm" onClick={() => handleBulkAction('read')}>Mark Read</button>
              {filter === 'archived' ? (
                <button className="btn btn-primary btn-sm" onClick={() => handleBulkAction('restore')}>Restore</button>
              ) : (
                <button className="btn btn-secondary btn-sm" onClick={() => handleBulkAction('archive')}>Archive</button>
              )}
              <button className="btn btn-danger btn-sm" onClick={() => handleBulkAction('delete')}>Delete</button>
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div className="notifications-list">
          {notifications.length > 0 ? (
            <>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => !notification.is_read && handleMarkAsRead(notification.id)}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}
                >
                  <input
                    type="checkbox"
                    checked={selectedNotifications.has(notification.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelect(notification.id);
                    }}
                    style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
                  />

                  <div className="notification-icon" style={{ color: getNotificationColor(notification.type) }}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="notification-content" style={{ flex: 1 }}>
                    <div className="notification-header">
                      <h4 className="notification-title">{notification.title}</h4>
                      <div className="notification-meta" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="notification-time">{formatTimeAgo(notification.created_at)}</span>
                        {!notification.is_read && <span className="notification-unread-dot"></span>}

                        <div className="notification-actions" style={{ display: 'flex', gap: '8px', opacity: 0.6 }}>
                          {archivedIds.has(notification.id) ? (
                            <button
                              className="btn-icon"
                              onClick={(e) => { e.stopPropagation(); handleRestore(notification.id); }}
                              title="Restore"
                            >
                              ↩️
                            </button>
                          ) : (
                            <button
                              className="btn-icon"
                              onClick={(e) => { e.stopPropagation(); handleArchive(notification.id); }}
                              title="Archive"
                            >
                              📥
                            </button>
                          )}
                          <button
                            className="btn-icon"
                            onClick={(e) => { e.stopPropagation(); handleDelete(notification.id); }}
                            title="Delete Permanently"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>

                    <p className="notification-message">{notification.message}</p>

                    {notification.data && (
                      <div className="notification-data">
                        {notification.type === 'admission' && notification.data.student_id && (
                          <div className="notification-detail">
                            <span className="badge badge-info">Student: {notification.data.student_id}</span>
                            {notification.data.commission && (
                              <span className="badge badge-success">+KES {notification.data.commission}</span>
                            )}
                          </div>
                        )}

                        {notification.type === 'enrollment' && notification.data.student_id && (
                          <div className="notification-detail">
                            <span className="badge badge-info">Student: {notification.data.student_id}</span>
                            {notification.data.commission && (
                              <span className="badge badge-success">+KES {notification.data.commission}</span>
                            )}
                          </div>
                        )}

                        {notification.type === 'payment' && notification.data.amount && (
                          <div className="notification-detail">
                            <span className="badge badge-success">Amount: KES {notification.data.amount.toLocaleString()}</span>
                            {notification.data.reference && (
                              <span className="badge badge-info">Ref: {notification.data.reference}</span>
                            )}
                          </div>
                        )}

                        {notification.type === 'achievement' && notification.data.milestone && (
                          <div className="notification-detail">
                            <span className="badge badge-warning">Milestone: {notification.data.milestone} {notification.data.type}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {!notification.is_read && (
                    <button
                      className="notification-mark-read"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                      title="Mark as read"
                    >
                      ✓
                    </button>
                  )}
                </div>
              ))}
              <Pagination
                pagination={pagination}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                loading={loading}
              />
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
              <div style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.5 }}>🔔</div>
              <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Notifications</h3>
              <p style={{ fontSize: '14px', marginBottom: '24px' }}>
                {filter === 'all'
                  ? "You don't have any notifications yet. Your recruitment activities will appear here."
                  : filter === 'archived'
                    ? "Your archived notifications will appear here."
                    : `No ${filter} notifications found. Try selecting a different filter.`
                }
              </p>
              {filter !== 'all' && (
                <button
                  className="btn btn-outline"
                  onClick={() => setFilter('all')}
                >
                  View All Notifications
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

